import { Dispatch, SetStateAction, useState } from 'react';
import {
  Button,
  Card,
  Col,
  Form,
  Row,
  Spinner,
  Tab,
  Tabs,
} from 'react-bootstrap';
import { SubmitHandler, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { faGear, faLock } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  APP_EVENTS,
  APPLICATION_SETTING,
  captureAppEvent,
} from 'src/services/posthogEvents';

import useRemoteBoundStore from 'src/store/useRemoteBoundStore';

import { Api } from 'src/models/Api.model';
import {
  CustomField,
  CustomPracticeProfileFieldEnum,
} from 'src/models/GalenData/CustomField.model';
import { CustomFieldData } from 'src/models/GalenData/CustomFieldData.model';
import { GalenErrorKeys } from 'src/models/GalenData/GalenError.model';
import {
  convertPracticeSettings,
  convertUserSettings,
  getPracticeDirtyFields,
} from 'src/models/GalenData/helpers/saveSettings';
import { adminRoleList } from 'src/models/GalenData/UserRole.model';
import { I18NextNS } from 'src/models/Language.model';

import useGetCurrentUser from 'src/hooks/useGetCurrentUser';
import useGetGalenErrorMsg from 'src/hooks/useGetGalenErrorMsg';
import useGetUsers from 'src/hooks/useGetUsers';
import useHandleHttpRequestError from 'src/hooks/useHandleHttpRequestError';

import CameraSettings from './CameraSettingsTab';

enum TabEnum {
  Camera = 'Camera',
  Application = 'Application',
}

export type SettingFormDataTypes = {
  hasMfa: boolean;
};

interface ISettingsTabProps {
  ipList: string[];
  setIpList: Dispatch<SetStateAction<string[]>>;
}

const SettingsTab = ({ ipList, setIpList }: ISettingsTabProps) => {
  const { setPracticeSettings, setUserSettings } = useRemoteBoundStore();

  const { currentUser, userId, practiceId, userType } = useGetCurrentUser();

  const [key, setKey] = useState<TabEnum>(TabEnum.Camera);

  const [practiceSettingsRawData, setPracticeSettingsRawData] =
    useState<CustomFieldData<CustomPracticeProfileFieldEnum>[]>();

  const [practiceCustomFields, setPracticeCustomFields] = useState<
    CustomField<CustomPracticeProfileFieldEnum>[]
  >([]);

  const { getErrorMessage } = useGetGalenErrorMsg();

  const fetchCustomFieldDefinitions = async () => {
    try {
      const practiceCustomFieldsResponse =
        await Api.remote.getPracticeCustomFieldDefinitions();
      setPracticeCustomFields(practiceCustomFieldsResponse.data.content ?? []);
    } catch (error) {
      showHttpRequestError(error, {
        messagePrefix: t('message.error.failedToLoadSettings', {
          ns: I18NextNS.COMMON,
        }),
      });
      setPracticeCustomFields([]);
    }
  };

  const {
    register,
    handleSubmit,
    reset,
    formState: { isDirty, dirtyFields, isLoading, isSubmitting },
  } = useForm({
    defaultValues: async () => {
      try {
        if (!practiceId) {
          throw new Error(getErrorMessage(GalenErrorKeys.PRACTICE_ID_REQUIRED));
        }

        if (!userId) {
          throw new Error(
            t('message.error.userIdNotFound', { ns: I18NextNS.COMMON }),
          );
        }

        await fetchCustomFieldDefinitions();

        const practiceSettings =
          await Api.remote.getPracticeSettings(practiceId);

        setPracticeSettingsRawData(practiceSettings.data);

        const practiceSettingsData = convertPracticeSettings(
          practiceSettings.data,
        );

        return {
          ...practiceSettingsData,
        };
      } catch (error) {
        showHttpRequestError(error, {
          messagePrefix: t('message.error.failedToLoadSettings', {
            ns: I18NextNS.COMMON,
          }),
        });
        throw error;
      }
    },
  });

  const {
    t,
    i18n: { language },
  } = useTranslation(I18NextNS.PAGES);

  const { showHttpRequestError } = useHandleHttpRequestError();

  const onSubmit: SubmitHandler<{
    hasMfa: boolean;
  }> = async (data) => {
    try {
      if (!currentUser?.currentRole?.practice) {
        throw new Error(getErrorMessage(GalenErrorKeys.PRACTICE_IS_REQUIRED));
      }

      const practiceDirtyFields = getPracticeDirtyFields(dirtyFields);

      if (practiceDirtyFields.length > 0) {
        await Api.remote.savePracticeSettings(
          practiceDirtyFields,
          practiceSettingsRawData,
          data,
          currentUser.currentRole.practice,
          practiceCustomFields,
        );
      }

      if (dirtyFields.hasMfa) {
        captureAppEvent(APP_EVENTS.APPLICATION_SETTING_CHANGED, {
          setting: APPLICATION_SETTING.MFA_REQUIRED,
          value: data.hasMfa,
        });
      }

      reset({ ...data });

      if (!practiceId) {
        throw new Error(getErrorMessage(GalenErrorKeys.PRACTICE_ID_REQUIRED));
      }

      if (!userId) {
        throw new Error(getErrorMessage(GalenErrorKeys.USER_ID_REQUIRED));
      }

      await fetchCustomFieldDefinitions();

      const [userSettings, practiceSettings] = await Promise.all([
        Api.remote.getUserSettings(userId),
        Api.remote.getPracticeSettings(practiceId),
      ]);

      const userSettingsData = convertUserSettings(userSettings.data);
      const practiceSettingsData = convertPracticeSettings(
        practiceSettings.data,
      );

      setUserSettings({ userSettings: userSettingsData });
      setPracticeSettings({ practiceSettings: practiceSettingsData });

      Api.alertBox({
        title: t('bootbox.title.Success', { ns: I18NextNS.COMMON }),
        message: t('message.success.updateSettings', { ns: I18NextNS.COMMON }),
        locale: language,
        type: 'success',
      });
    } catch (error) {
      showHttpRequestError(error);
    }
  };

  const { handleGetUsers } = useGetUsers();

  const handleBulkMFA = async (MFA: boolean) => {
    try {
      const fetchedUsers = await handleGetUsers();
      const promises =
        fetchedUsers.content?.map((user) =>
          Api.remote.set_user_mfa(MFA, user.userId),
        ) || [];
      await Promise.all(promises);

      Api.alertBox({
        title: t('bootbox.title.Information', { ns: I18NextNS.COMMON }),
        message: t('settings.applicationForm.fields.MFAbulk.success'),
        locale: language,
        type: 'success',
      });
    } catch (error) {
      showHttpRequestError(error);
    }
  };

  const isAdmin = userType && adminRoleList.includes(userType);

  const handleSave = handleSubmit(onSubmit);

  return (
    <div className="mb-4 overflow-y-auto overflow-x-hidden">
      <div className="d-flex justify-content-between align-items-center">
        <h3 data-test="settings-page-title">
          {t(`settings.tabs.${key}`)} {t('settings.title')}
        </h3>

        {key === TabEnum.Application && (
          <div>
            <Button
              onClick={handleSave}
              variant="primary"
              disabled={!isDirty || isSubmitting}
              data-test="settings-save-settings-button"
              data-track="settings.general.save"
              data-observe
            >
              <FontAwesomeIcon icon={faGear} className="me-2" />
              {isSubmitting
                ? `${t('settings.applicationForm.saving')}...`
                : t('settings.applicationForm.saveSettings')}
            </Button>
          </div>
        )}
      </div>

      <Tabs
        id="controlled-tab-example"
        activeKey={key}
        onSelect={(k) => {
          setKey(k as TabEnum);
          reset();
        }}
        className="mt-3"
      >
        <Tab
          eventKey={TabEnum.Camera}
          title={t(`settings.tabs.Camera`)}
          tabClassName="text-primary"
          tabAttrs={{ 'data-test': 'settings-camera-tab' }}
        >
          <CameraSettings ipList={ipList} setIpList={setIpList} />
        </Tab>

        {isAdmin && (
          <Tab
            eventKey={TabEnum.Application}
            title={t(`settings.tabs.Application`)}
            className="pt-3"
            tabClassName="text-primary"
            tabAttrs={{ 'data-test': 'settings-application-tab' }}
          >
            <Form onSubmit={handleSubmit(onSubmit)}>
              <Row>
                {isLoading ? (
                  <div
                    className="d-flex justify-content-center align-items-center"
                    style={{ minHeight: 400 }}
                  >
                    <Spinner variant="primary" />
                  </div>
                ) : (
                  <>
                    <Col md={6}>
                      <Card className="data-container mt-2 p-0 shadow">
                        <Card.Body>
                          {isAdmin && (
                            <div data-test="mfa-card">
                              <Form.Group className="pt-1">
                                <Form.Label>
                                  <FontAwesomeIcon
                                    icon={faLock}
                                    className="me-2"
                                  />
                                  <strong>
                                    {t(
                                      'settings.applicationForm.fields.MFA.title',
                                    )}
                                  </strong>
                                </Form.Label>
                                <Form.Check
                                  type="switch"
                                  id="hasMfa"
                                  label={t(
                                    'settings.applicationForm.fields.MFA.field',
                                  )}
                                  {...register('hasMfa')}
                                  disabled={!isAdmin}
                                  data-test="settings-mfa-by-default-switch"
                                />
                                <small className="text-primary">
                                  {t(
                                    'settings.applicationForm.fields.MFA.desc',
                                  )}
                                </small>
                              </Form.Group>
                              <div className="d-flex mt-4 gap-4">
                                <Button
                                  variant="primary"
                                  data-test="settings-enable-mfa-for-all-users-button"
                                  data-track="settings.bulkmfa.confirm.on"
                                  data-observe
                                  onClick={() => {
                                    Api.confirmBox({
                                      title: t('bootbox.title.Confirm', {
                                        ns: I18NextNS.COMMON,
                                      }),
                                      message: t(
                                        'settings.applicationForm.fields.MFAbulk.confirmOn',
                                      ),
                                      locale: language,
                                      callback: (result) => {
                                        if (result) {
                                          handleBulkMFA(true);
                                        }
                                      },
                                    });
                                  }}
                                >
                                  {t(
                                    'settings.applicationForm.fields.MFAbulk.on',
                                  )}
                                </Button>
                                <Button
                                  variant="danger"
                                  data-test="settings-disable-mfa-for-all-users-button"
                                  data-track="settings.bulkmfa.confirm.off"
                                  data-observe
                                  onClick={() => {
                                    Api.confirmBox({
                                      title: t('bootbox.title.Confirm', {
                                        ns: I18NextNS.COMMON,
                                      }),
                                      message: t(
                                        'settings.applicationForm.fields.MFAbulk.confirmOff',
                                      ),
                                      locale: language,
                                      callback: (result) => {
                                        if (result) {
                                          handleBulkMFA(false);
                                        }
                                      },
                                    });
                                  }}
                                >
                                  {t(
                                    'settings.applicationForm.fields.MFAbulk.off',
                                  )}
                                </Button>
                              </div>
                              <small className="text-primary">
                                {t(
                                  'settings.applicationForm.fields.MFAbulk.desc',
                                )}
                              </small>
                            </div>
                          )}
                        </Card.Body>
                      </Card>
                    </Col>
                  </>
                )}
              </Row>
            </Form>
          </Tab>
        )}
      </Tabs>
    </div>
  );
};

export default SettingsTab;
